import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { fetchPrograms } from "@/lib/api";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";
import { SITE_URL } from "@/lib/site";
import SEO from "@/components/SEO";

export default function Programs() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetchPrograms().then(setPrograms).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsonLd = programs.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Weight Loss Gurukul Programs",
        itemListElement: programs.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE_URL}/programs/${p.slug}`,
          name: p.title,
        })),
      }
    : null;

  return (
    <div data-testid="programs-page" className="section-pad">
      <SEO
        title="Best Online Yoga Programs for Weight Loss — 7-Day, 90-Day & 1-Year | Weight Loss Gurukul"
        description="The best 1-on-1 online yoga programs for weight loss at home — start with a complimentary 7-Day Transformation Starter, commit to the 90-Day Inner Alchemy Challenge, or transform end-to-end with the VIP 1-Year Total Transform Challenge."
        keywords="best yoga program for weight loss, online yoga weight loss program, 90 day yoga challenge, yoga transformation program, 1 on 1 yoga coaching, yoga for weight loss at home, weight loss yoga India"
        path="/programs"
        jsonLd={jsonLd}
      />
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="eyebrow">Programs</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl text-forest leading-tight">
            Every journey has a path. Find yours.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-ink-soft">
            Structured live programs built around a single promise — consistent daily
            practice, expert guidance, and measurable transformation. Join from any
            country, at any level.
          </p>
        </div>

        <div className="mt-12 md:mt-14 grid md:grid-cols-2 gap-5 md:gap-6">
          {programs.map((p) => {
            const msg = WA_MESSAGES[p.cta_message_key] || WA_MESSAGES.program(p.title);
            return (
              <article
                key={p.slug}
                data-testid={`programs-page-card-${p.slug}`}
                className="group rounded-3xl border border-sand-300 bg-white p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-sand-200 text-forest">{p.duration}</span>
                  <span className="text-ink-muted">· {p.level}</span>
                </div>
                <h2 className="mt-4 font-serif text-2xl md:text-3xl text-forest group-hover:text-clay transition-colors leading-tight">{p.title}</h2>
                <p className="mt-2 text-clay font-medium italic">{p.tagline}</p>
                <p className="mt-3 text-sm text-ink-soft">{p.benefit}</p>
                <p className="mt-4 text-sm text-ink-soft leading-relaxed flex-1">{p.description}</p>

                {p.price_display && (
                  <div className="mt-5 pt-5 border-t border-sand-300 flex items-baseline gap-3 flex-wrap">
                    {p.price_strike && (
                      <span className="text-sm line-through text-ink-muted">{p.price_strike}</span>
                    )}
                    <span className="font-serif text-3xl text-forest leading-none">{p.price_display}</span>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-sand-300 flex flex-wrap items-center gap-3">
                  <a
                    href={waLink(msg)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`programs-page-cta-${p.slug}`}
                    className="btn-accent text-sm"
                  >
                    <MessageCircle className="h-4 w-4" /> Book Free Consultation
                  </a>
                  <Link
                    to={`/programs/${p.slug}`}
                    data-testid={`programs-page-details-${p.slug}`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:text-clay transition-all"
                  >
                    See full details <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
